'use client';

import { useCampaign } from '@/app/CampaignContext';
import dynamic from 'next/dynamic';
import ExternalLink from '@/components/elements/ExternalLink';
import Link from 'next/link';
import { formatHighlightedText } from '@/lib/utils';

const ProductCard = dynamic(() => import('./ProductCard'));
const TabProductCard = dynamic(() => import('./TabProductCard'));
const FillerCard = dynamic(() => import('./FillerCard'));
const CollectionCard = dynamic(() => import('./CollectionCard'));
const BundleCard = dynamic(() => import('./BundleCard'));

export default function ProductGroup({ block, position, template }) {
  const campaign = useCampaign();

  const {
    background_color,
    cards,
    button_text,
    preheading,
    heading,
    description,
    heading_text_secondary_color,
    heading_style,
    heading_link,
    padding,
    uid
  } = block?.product_group?.reference?.[0] ?? {};

  const collectionPage = template == 'Collection' ?? false;

  if (!cards || cards.length === 0) {
    return null;
  }

  const headingStyle = heading_style || 'Default, Left';
  const yPadding = padding || 'Default';

  return (
    <section
      id={`${uid}_${position}`}
      className={`${yPadding == 'Large' ? 'py-15 lg:py-20' : 'py-15'} bg-[${background_color ?? '#f6f5f5'}]`}
      data-optimizely="product-group"
      data-name="product-group"
    >
      <div className="mx-auto max-w-[1360px] px-5 sm:px-10 lg:px-20">
        {(heading || description) && (
          <Heading
            preheading={preheading}
            heading={heading}
            description={description}
            headingStyle={headingStyle}
            headingLink={heading_link}
            headingSecondaryColor={heading_text_secondary_color}
          />
        )}
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card, cardIndex) => {
            const cardTypeData = card?.card_type?.[0];
            if (!cardTypeData) return null;

            const typeName = Object.keys(cardTypeData)[0];
            switch (typeName) {
              case 'product':
                return (
                  <ProductCard
                    key={`product-card-${cardIndex}`}
                    index={cardIndex}
                    cardData={card}
                    button_text={button_text}
                    campaign={campaign}
                    collectionPage={collectionPage}
                  />
                );
              case 'tabbed_product':
                return (
                  <TabProductCard
                    key={`tabbed-card-${cardIndex}`}
                    index={cardIndex}
                    cardData={card}
                    button_text={button_text}
                    campaign={campaign}
                    collectionPage={collectionPage}
                  />
                );
              case 'filler':
                return (
                  <FillerCard
                    key={`filler-card-${cardIndex}`}
                    index={cardIndex}
                    cardData={card}
                    collectionPage={collectionPage}
                  />
                );
              case 'collection':
                return (
                  <CollectionCard
                    key={`collection-card-${cardIndex}`}
                    index={cardIndex}
                    cardData={card}
                    button_text={button_text}
                    campaign={campaign}
                    collectionPage={collectionPage}
                  />
                );
              case 'bundle':
                return (
                  <BundleCard
                    key={`bundle-card-${cardIndex}`}
                    index={cardIndex}
                    cardData={card}
                    buttonText={button_text}
                    collectionPage={collectionPage}
                  />
                );
              default:
                return null;
            }
          })}
        </div>
      </div>
    </section>
  );
}

const Heading = ({
  preheading,
  heading,
  description,
  headingStyle,
  headingLink,
  headingSecondaryColor
}) => {
  const formattedHeading = formatHighlightedText(heading, {
    color: headingSecondaryColor
  });

  return (
    <>
      {headingStyle == 'Large, Center' ? (
        <div className="mx-auto max-w-[800px] pb-15 text-center">
          {preheading && (
            <div className="preheading-s lg:preheading-r mb-4">
              {preheading}
            </div>
          )}
          {heading && <h2 className="h3 lg:h2">{formattedHeading}</h2>}
          {description && <p className="body-l lg:sh8 pt-4">{description}</p>}
          {headingLink?.label && (
            <div className="mx-auto mt-5 flex justify-center">
              <HeadingLink headingLink={headingLink} />
            </div>
          )}
        </div>
      ) : headingStyle == 'Large, Left' ? (
        <div className="pb-10 lg:flex lg:justify-between">
          <div className="max-w-[800px]">
            {preheading && (
              <div className="preheading-s lg:preheading-r mb-4">
                {preheading}
              </div>
            )}
            {heading && <h2 className="h3 lg:h2">{formattedHeading}</h2>}
            {description && (
              <p className="body-s sm:body-r pt-2">{description}</p>
            )}
          </div>
          {headingLink?.label && (
            <HeadingLink headingLink={headingLink} styles="mt-5 lg:mt-0" />
          )}
        </div>
      ) : (
        <div className="flex justify-between pb-10">
          <div className="max-w-[600px]">
            {preheading && (
              <div className="preheading-s lg:preheading-r mb-4">
                {preheading}
              </div>
            )}
            {heading && <h2 className="sh8 sm:sh7">{formattedHeading}</h2>}
            {description && (
              <p className="body-s sm:body-r pt-2">{description}</p>
            )}
          </div>
          {headingLink?.label && <HeadingLink headingLink={headingLink} />}
        </div>
      )}
    </>
  );
};

const HeadingLink = ({ headingLink, styles }) => {
  return (
    <div className={`h6 lg:h5 flex cursor-pointer items-end ${styles}`}>
      {headingLink.link_type == 'Internal Link' ? (
        <Link
          href={`${headingLink?.internal_link[0]?.url}${headingLink?.query_string}${headingLink?.anchor}`}
          prefetch={false}
          className="txt-btn-secondary"
        >
          {headingLink.label}
        </Link>
      ) : (
        <ExternalLink
          linkType={headingLink?.link_type}
          linkStyling="txt-btn-secondary"
          externalLink={headingLink?.external_link}
          linkText={headingLink.label}
        >
          {headingLink.label}
        </ExternalLink>
      )}
      <GoArrow />
    </div>
  );
};

const GoArrow = () => {
  return (
    <svg
      enableBackground="new 0 0 24 24"
      height="24"
      width="24"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className="ml-[10px] self-end"
      aria-hidden="true"
    >
      <g
        fill="none"
        stroke="#000000"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m3 12h18" />
        <path d="m16 7 5 5-5 5" />
      </g>
    </svg>
  );
};
